import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import PhysicalSalesBanner, { useSalesStatus } from "../components/PhysicalSalesBanner";
import { api, trackEvent } from "../lib/api";
import { tr } from "../i18n/translations";
import { Minus, Plus, Trash2, Truck, Gift } from "lucide-react";
import Seo from "../components/Seo";
import OrthoTicket from "../components/OrthoTicket";
import ShippingInfoBlock from "../components/ShippingInfoBlock";
import { toast } from "sonner";

const COUNTRIES = [
  { code: "FR", name: "France" }, { code: "BE", name: "Belgique" }, { code: "CH", name: "Suisse" },
  { code: "DE", name: "Deutschland" }, { code: "IT", name: "Italia" }, { code: "ES", name: "España" },
  { code: "PT", name: "Portugal" }, { code: "NL", name: "Nederland" }, { code: "LU", name: "Luxembourg" },
  { code: "AT", name: "Österreich" }, { code: "IE", name: "Ireland" }, { code: "DK", name: "Danmark" },
  { code: "SE", name: "Sverige" }, { code: "FI", name: "Suomi" }, { code: "PL", name: "Polska" },
  { code: "RO", name: "România" }, { code: "GR", name: "Ελλάδα" }, { code: "CZ", name: "Česko" },
  { code: "HU", name: "Magyarország" }, { code: "BG", name: "България" }, { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" }, { code: "CA", name: "Canada" }, { code: "IL", name: "ישראל" },
  { code: "MA", name: "Maroc" }, { code: "TN", name: "Tunisie" }, { code: "DZ", name: "Algérie" },
  { code: "ZZ", name: "Autre / Other" },
];

export default function Cart() {
  const { cart, updateQty, removeItem, cartTotal, lang, fmt } = useApp();
  const { should_block_physical } = useSalesStatus();
  // Detection robuste : un item est "physique" s'il n'est ni is_digital ni no_shipping.
  const cartHasPhysicalItem = cart.some((it) => !it.is_digital && !it.no_shipping);
  const [discount, setDiscount] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [giftCode, setGiftCode] = useState("");
  const [appliedGift, setAppliedGift] = useState(null);
  const [appliedProductGift, setAppliedProductGift] = useState(null); // Code cadeau concours (multi-produits)
  const [digitalWaiver, setDigitalWaiver] = useState(false);
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState(() => localStorage.getItem("country") || "FR");
  const [vatNumber, setVatNumber] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  // Fallback fiable pour paniers legacy (no_shipping absent dans localStorage) :
  // si le breakdown backend renvoie une portion physique = 0, on sait que le panier
  // est 100% digital et on doit NE PAS bloquer le checkout, même si le flag localStorage ment.
  const cartIsAllDigital = breakdown && (breakdown.subtotal_ht - (breakdown.excluded_subtotal_ht || 0)) <= 0.001;
  const blockedByPhysicalSales = should_block_physical && cartHasPhysicalItem && !cartIsAllDigital;
  const [loading, setLoading] = useState(false);
  const [shipName, setShipName] = useState("");
  const [shipLine1, setShipLine1] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipPostal, setShipPostal] = useState("");
  const [shipPhone, setShipPhone] = useState("");
  const [loyaltyAccount, setLoyaltyAccount] = useState(null);
  // tickets_claimed: { "20": n, "100": n, "200": n }
  const [tickets, setTickets] = useState({});
  useEffect(() => { localStorage.setItem("country", country); }, [country]);

  // Restore identity from localStorage + auto-fill from client account (Item 32)
  useEffect(() => {
    const e = localStorage.getItem("lo_email");
    if (e && !email) setEmail(e);
    const token = localStorage.getItem("client_token");
    if (token) {
      api.get("/clients/me", { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          const u = r.data;
          if (u?.email) { setEmail(u.email); localStorage.setItem("lo_email", u.email); }
          if (u?.full_name && !shipName) setShipName(u.full_name);
          if (u?.phone && !shipPhone) setShipPhone(u.phone);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lookup orthopoints balance when email is valid (debounced 800ms)
  useEffect(() => {
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!validEmail) { setLoyaltyAccount(null); setTickets({}); return; }
    const t = setTimeout(() => {
      api.get(`/loyalty/account/${encodeURIComponent(email.trim().toLowerCase())}`)
        .then((r) => setLoyaltyAccount(r.data))
        .catch(() => setLoyaltyAccount(null));
    }, 800);
    return () => clearTimeout(t);
  }, [email]);

  // Log abandoned cart when email + items present (debounced 2s)
  useEffect(() => {
    if (cart.length === 0) return;
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!validEmail) return;
    const t = setTimeout(() => {
      const items = cart.map((c) => ({ name: c.name, price: c.price, quantity: c.quantity, slug: c.slug }));
      const amount = cart.reduce((a, b) => a + b.price * b.quantity, 0);
      api.post("/cart/abandoned", { email, session_id: localStorage.getItem("session_id"), items, amount, lang }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, [email, cart, lang]);

  useEffect(() => {
    if (cart.length === 0) { setBreakdown((b) => b ? null : b); return; }
    const hasTickets = Object.values(tickets).some((v) => v > 0);
    const payload = {
      items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
      origin_url: window.location.origin,
      country,
      vat_number: vatNumber || undefined,
      discount_code: appliedDiscount?.code,
      gift_card_code: appliedGift?.code,
      product_gift_code: appliedProductGift?.code,
      loyalty_email: hasTickets ? email : undefined,
      tickets_claimed: hasTickets ? tickets : undefined,
    };
    let cancelled = false;
    api.post("/checkout/preview", payload)
      .then((r) => { if (!cancelled) setBreakdown(r.data); })
      .catch(() => { if (!cancelled) setBreakdown(null); });
    return () => { cancelled = true; };
  }, [cart, country, vatNumber, appliedDiscount, appliedGift, appliedProductGift, email, tickets]);

  async function applyDiscount() {
    if (!discount) return;
    try {
      const r = await api.get(`/discount-codes/check?code=${encodeURIComponent(discount)}`);
      const data = r.data;
      if (data.type === "gift_card") {
        // C'était en fait un bon cadeau monétaire → l'appliquer comme tel
        setAppliedGift(data);
        setAppliedDiscount(null);
        setGiftCode(data.code);
        toast.success(`Bon cadeau ${data.code} appliqué (${data.remaining} €)`);
      } else if (data.type === "product_gift") {
        // Code cadeau concours → afficher les cadeaux DANS le panier, pas de redirect.
        // ⚠️ Ne PAS écraser un code promo % éventuellement déjà appliqué : les deux
        // sont cumulables (le cadeau met des items à 0€, le code promo applique le %
        // sur les autres items du panier).
        setAppliedProductGift(data);
        setDiscount("");
        const nbProducts = data.product_count || 1;
        const totalValue = data.total_value_eur || data.product_refs?.reduce((s, r) => s + (r.value_eur || 0), 0) || 0;
        toast.success(
          `🎁 ${nbProducts} cadeau${nbProducts > 1 ? "x" : ""} ajouté${nbProducts > 1 ? "s" : ""} (valeur ${totalValue.toFixed(2)} €) !`,
          { duration: 4000 }
        );
      } else {
        // Code promo % classique
        setAppliedDiscount(data);
        toast.success(`Code ${data.code} (-${data.percent}%)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
      setAppliedDiscount(null);
    }
  }
  async function applyGift() {
    if (!giftCode) return;
    try {
      const r = await api.get(`/gift-cards/check?code=${encodeURIComponent(giftCode)}`);
      setAppliedGift(r.data);
      toast.success(`Bon ${r.data.code} (${r.data.remaining} €)`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Bon invalide";
      if (typeof detail === "string" && detail.includes("/reclamer-cadeau")) {
        toast.error(detail, { duration: 6000 });
        setTimeout(() => { window.location.href = `/reclamer-cadeau?code=${encodeURIComponent(giftCode.toUpperCase().trim())}`; }, 1200);
      } else {
        toast.error(detail);
      }
      setAppliedGift(null);
    }
  }

  // Un cadeau concours contient-il un item physique ?
  const giftHasPhysical = !!(appliedProductGift?.requires_shipping ||
    appliedProductGift?.product_refs?.some((r) => r.type === "product"));
  // Doit-on demander l'adresse de livraison ?
  const cartHasPhysical = !!(breakdown?.subtotal_ht > 0);
  const needsShipping = cartHasPhysical || giftHasPhysical;

  // Total effectif TTC (0 si panier vide + cadeau concours pur)
  const effectiveTotal = cart.length === 0 && appliedProductGift ? 0 : (breakdown?.total_ttc ?? cartTotal);
  const isFreeCheckout = effectiveTotal <= 0.01;

  async function checkout() {
    if (cart.length === 0 && !appliedProductGift) return;
    if (blockedByPhysicalSales) {
      toast.error("Vente physique temporairement suspendue — retire les articles physiques du panier pour continuer.");
      return;
    }
    if (!email) { toast.error("Email requis pour le reçu PDF"); return; }
    if (needsShipping && (!shipName || !shipLine1 || !shipCity || !shipPostal)) {
      toast.error("Adresse de livraison incomplète"); return;
    }
    // Free checkout : on considère la renonciation au droit de rétractation comme
    // implicite (aucun paiement, livraison numérique immédiate). Auto-coche la case.
    if (isFreeCheckout && breakdown?.digital_ttc > 0 && !digitalWaiver) {
      setDigitalWaiver(true);
    }
    setLoading(true);
    trackEvent("checkout_start", {
      quantity: cart.reduce((a, b) => a + b.quantity, 0),
      amount: cart.reduce((a, b) => a + b.price * b.quantity, 0),
      free: isFreeCheckout,
      has_gift_code: !!appliedProductGift,
    });
    try {
      const aff = (await import("../lib/affiliateTracker")).getAffiliate();
      const commonBody = {
        items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
        origin_url: window.location.origin,
        discount_code: appliedDiscount?.code,
        gift_card_code: appliedGift?.code,
        product_gift_code: appliedProductGift?.code,
        country,
        vat_number: vatNumber || undefined,
        customer_email: email,
        shipping_address: needsShipping
          ? { name: shipName, line1: shipLine1, city: shipCity, postal: shipPostal, country, phone: shipPhone }
          : undefined,
        loyalty_email: Object.values(tickets).some((v) => v > 0) ? email : undefined,
        tickets_claimed: Object.values(tickets).some((v) => v > 0) ? tickets : undefined,
        affiliate_code: aff || undefined,
      };
      if (isFreeCheckout) {
        // Commande gratuite : bypass PayPal
        const r = await api.post("/checkout/free", commonBody);
        toast.success(r.data.message || "Commande validée !");
        // Redirection vers page succès
        const orderRef = r.data.order_id || r.data.gift_order_id;
        window.location.href = orderRef ? `/success?order_id=${orderRef}&free=1` : "/mes-ressources";
      } else {
        // PayPal classique
        const r = await api.post("/checkout/session", commonBody);
        window.location.href = r.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoading(false);
    }
  }

  if (cart.length === 0 && !appliedProductGift) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="cart-empty">
        <Seo title="Panier" path="/cart" noindex />
        <h1 className="font-display text-5xl font-light italic mb-4">{tr(lang, "cart_empty")}</h1>
        <Link to="/catalogue" className="btn-primary mt-6 inline-flex">{tr(lang, "cart_continue")}</Link>
      </div>
    );
  }

  // Clés éligibles pour les items OFFERTS (miroir backend _apply_gift_freebies)
  //   product::<slug> | resource::<id> | pack::<id>
  const giftedKeys = new Set(
    (appliedProductGift?.product_refs || []).map((r) => {
      const t = (r.type || "").toLowerCase();
      if (t === "resource" && r.id) return `resource::${r.id}`;
      if (t === "pack" && r.id) return `pack::${r.id}`;
      return `product::${(r.slug || "").toLowerCase()}`;
    })
  );
  const itemGiftKey = (it) => {
    const pid = it.product_id || "";
    if (pid.startsWith("resource-")) return `resource::${pid.slice("resource-".length)}`;
    if (pid.startsWith("pack-")) return `pack::${pid.slice("pack-".length)}`;
    return `product::${(it.slug || "").toLowerCase()}`;
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
      <Seo title="Panier" path="/cart" noindex />
      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-light mb-6 md:mb-8">{tr(lang, "cart")}</h1>
      {blockedByPhysicalSales && (
        <div className="mb-8">
          <PhysicalSalesBanner variant="inline" />
        </div>
      )}
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 lg:gap-12">
        <div className="space-y-4 min-w-0" data-testid="cart-items">
          {cart.map((it) => {
            const isGifted = giftedKeys.has(itemGiftKey(it));
            return (
            <div key={it.product_id} className={`flex gap-3 sm:gap-4 rounded-3xl p-3 sm:p-4 ${isGifted ? "bg-sage/5 border-2 border-sage/40" : "bg-white border border-ink/10"}`}>
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-ivory-200 flex-shrink-0">
                <img src={it.image} alt={it.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-base sm:text-lg leading-tight break-words">
                  {it.name}
                  {isGifted && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-sage-dark font-medium">🎁 Offert</span>}
                </p>
                <p className="text-xs sm:text-sm text-ink-soft mt-1">
                  {isGifted ? (
                    <>
                      <span className="line-through text-ink-soft/60">{fmt(it.price)}</span>
                      <span className="ml-2 font-medium text-sage-dark">Gratuit</span>
                    </>
                  ) : (
                    <>{fmt(it.price)} <span className="text-xs">{it.is_digital ? "TTC" : tr(lang, "price_ht")}</span></>
                  )}
                </p>
                <div className="mt-2 sm:mt-3 flex items-center gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center bg-ivory-200 rounded-full">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2"><Minus size={14} /></button>
                    <span className="px-2 text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-2"><Plus size={14} /></button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="text-ink-soft hover:text-coral-dark p-1" data-testid={`cart-remove-${it.product_id}`} aria-label="Retirer">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className={`font-display text-base sm:text-xl whitespace-nowrap self-start ${isGifted ? "line-through text-ink-soft/50" : ""}`}>{fmt(it.price * it.quantity)}</p>
            </div>
            );
          })}

          {/* Cadeaux concours à livrer (multi-produits) */}
          {appliedProductGift && (
            <div
              className="bg-gradient-to-br from-coral/10 to-sage/10 border-2 border-coral/40 rounded-3xl p-4 sm:p-5 mt-2"
              data-testid="cart-product-gift-section"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="font-display text-lg text-coral-dark flex items-center gap-2">
                    🎁 Cadeaux offerts
                  </p>
                  <p className="text-xs text-ink-soft mt-0.5">
                    Code <strong className="text-ink font-mono">{appliedProductGift.code}</strong>
                    {appliedProductGift.campaign_name && <> · {appliedProductGift.campaign_name}</>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAppliedProductGift(null)}
                  className="text-ink-soft hover:text-coral-dark text-xs shrink-0 mt-1"
                  data-testid="cart-product-gift-remove"
                  aria-label="Retirer le code cadeau"
                >✕</button>
              </div>
              <div className="space-y-2">
                {(appliedProductGift.product_refs || []).map((r, idx) => (
                  <div
                    key={`${r.slug}-${idx}`}
                    className="flex items-center gap-3 bg-white/70 rounded-2xl p-2.5"
                    data-testid={`cart-gift-item-${r.slug}`}
                  >
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-ivory-200 flex-shrink-0">
                      {r.image ? (
                        <img src={r.image} alt={r.name} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-coral-dark">
                          <Gift size={22} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{r.name}</p>
                      <p className="text-[11px] text-ink-soft mt-0.5">
                        {r.type === "resource" ? "PDF numérique" : r.type === "pack" ? "Pack PDF" : "Produit physique"}
                        {r.value_eur ? ` · valeur ${r.value_eur.toFixed(2)} €` : ""}
                      </p>
                    </div>
                    <span className="text-sage font-medium text-sm whitespace-nowrap">Offert</span>
                  </div>
                ))}
              </div>
              {giftHasPhysical && (
                <p className="text-[11px] text-ink-soft mt-3 italic">
                  📦 Ce cadeau contient un article physique — veuillez remplir l'adresse de livraison ci-dessous.
                </p>
              )}
            </div>
          )}

          {/* Shipping address — displayed if physical items OR physical gift */}
          {needsShipping && (
          <div className="bg-white rounded-3xl border border-ink/10 p-5 sm:p-6 mt-4">
            <h3 className="font-display text-lg mb-4">{tr(lang, "cart_address")}</h3>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <input data-testid="ship-name" value={shipName} onChange={(e) => setShipName(e.target.value)} placeholder="Nom complet" className="w-full min-w-0 bg-ivory-200 rounded-full px-4 py-2.5 outline-none" />
              <input data-testid="ship-phone" value={shipPhone} onChange={(e) => setShipPhone(e.target.value)} placeholder="Téléphone" className="w-full min-w-0 bg-ivory-200 rounded-full px-4 py-2.5 outline-none" />
              <input data-testid="ship-line1" value={shipLine1} onChange={(e) => setShipLine1(e.target.value)} placeholder="Adresse" className="w-full min-w-0 sm:col-span-2 bg-ivory-200 rounded-full px-4 py-2.5 outline-none" />
              <input data-testid="ship-postal" value={shipPostal} onChange={(e) => setShipPostal(e.target.value)} placeholder="Code postal" className="w-full min-w-0 bg-ivory-200 rounded-full px-4 py-2.5 outline-none" />
              <input data-testid="ship-city" value={shipCity} onChange={(e) => setShipCity(e.target.value)} placeholder="Ville" className="w-full min-w-0 bg-ivory-200 rounded-full px-4 py-2.5 outline-none" />
              <select
                data-testid="ship-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full min-w-0 sm:col-span-2 bg-ivory-200 rounded-full px-4 py-2.5 outline-none"
              >
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>🌍 {c.name}</option>)}
              </select>
            </div>
            <p className="text-xs text-ink-soft mt-3 italic">{tr(lang, "shipping_notice")}</p>
          </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-ink/10 p-5 sm:p-6 h-fit lg:sticky lg:top-24 min-w-0">
          <input
            data-testid="cart-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email pour reçu PDF"
            className="w-full bg-ivory-200 rounded-full px-5 py-3 text-sm outline-none mb-3"
          />

          <select
            data-testid="cart-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full bg-ivory-200 rounded-full px-5 py-3 text-sm outline-none mb-3"
          >
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>

          <input
            data-testid="cart-vat-number"
            value={vatNumber}
            onChange={(e) => setVatNumber(e.target.value.toUpperCase())}
            placeholder={tr(lang, "cart_vat_number")}
            className="w-full bg-ivory-200 rounded-full px-5 py-3 text-sm outline-none mb-4"
          />

          {loyaltyAccount && loyaltyAccount.balance > 0 && (
            <LoyaltyRedeem
              account={loyaltyAccount}
              tickets={tickets}
              setTickets={setTickets}
              subtotal={breakdown?.subtotal_ht ?? cartTotal}
            />
          )}

          {/* Info hint : les 2 réductions sont cumulables */}
          <p className="text-[11px] text-ink-soft mb-2 flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-sage"></span>
            Cumulable : 1 code promo + 1 bon cadeau + orthopoints
          </p>

          {/* Row 1 : Discount code */}
          {appliedDiscount ? (
            <div className="mb-3" data-testid="cart-discount-applied">
              <div className="flex items-center justify-between gap-2 bg-sage/15 border border-sage/40 rounded-full px-4 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sage-dark text-xs">✓</span>
                  <span className="font-medium text-sm text-ink truncate">{appliedDiscount.code}</span>
                  <span className="text-xs text-ink-soft whitespace-nowrap">-{appliedDiscount.percent}%</span>
                </div>
                <button
                  type="button"
                  onClick={() => { setAppliedDiscount(null); setDiscount(""); }}
                  className="text-ink-soft hover:text-coral-dark text-xs shrink-0"
                  data-testid="cart-discount-remove"
                  aria-label="Retirer le code"
                >✕</button>
              </div>
              {appliedDiscount.expires_at && (
                <DiscountCountdown expiresAt={appliedDiscount.expires_at} onExpired={() => setAppliedDiscount(null)} />
              )}
            </div>
          ) : (
            <div className="mb-3">
              <div className="flex gap-2">
                <input
                  data-testid="cart-discount-input"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value.toUpperCase())}
                  placeholder="Code promo, bon cadeau ou code concours"
                  className="flex-1 min-w-0 bg-ivory-200 rounded-full px-4 py-2.5 text-sm outline-none"
                />
                <button data-testid="cart-discount-apply" onClick={applyDiscount} className="rounded-full bg-ink text-ivory text-sm px-4 py-2 whitespace-nowrap shrink-0">{tr(lang, "code_apply")}</button>
              </div>
              <p className="text-[10px] text-ink-soft mt-1.5 italic">Un seul champ pour tous vos codes — nous détectons le type automatiquement.</p>
            </div>
          )}

          {/* Bon cadeau appliqué (via champ unifié) */}
          {appliedGift && (
            <div className="mb-5 flex items-center justify-between gap-2 bg-coral/15 border border-coral/40 rounded-full px-4 py-2" data-testid="cart-gift-applied">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-coral-dark text-xs">🎁</span>
                <span className="font-medium text-sm text-ink truncate">{appliedGift.code}</span>
                <span className="text-xs text-ink-soft whitespace-nowrap">-{appliedGift.remaining}€</span>
              </div>
              <button
                type="button"
                onClick={() => { setAppliedGift(null); setGiftCode(""); }}
                className="text-ink-soft hover:text-coral-dark text-xs shrink-0"
                data-testid="cart-gift-remove"
                aria-label="Retirer le bon"
              >✕</button>
            </div>
          )}

          <div className="space-y-2 mb-5 text-sm">
            {breakdown?.subtotal_ht > 0 && <Row label={tr(lang, "cart_subtotal_ht")} value={breakdown?.subtotal_ht ?? cartTotal} />}
            {breakdown?.digital_ttc > 0 && <Row label="Ressources numériques (TTC)" value={breakdown.digital_ttc} />}
            {breakdown?.discount_pct > 0 && <Row label={`-${breakdown.discount_pct}%`} value={-((breakdown.subtotal_ht + (breakdown.digital_ttc || 0)) * breakdown.discount_pct / 100)} accent />}
            {breakdown?.gift_amount > 0 && <Row label={tr(lang, "gift_card")} value={-breakdown.gift_amount} accent />}
            {breakdown?.loyalty_used && (
              <Row
                label={`Orthoticket${breakdown.loyalty_used.points > 100 ? "s" : ""} (${breakdown.loyalty_used.points} orthopoints)`}
                value={-breakdown.loyalty_used.eur}
                accent
              />
            )}
            {breakdown?.subtotal_ht > 0 && <Row label={`${tr(lang, "cart_vat")} (${breakdown?.vat_rate ?? 0}%)`} value={breakdown?.vat_amount ?? 0} />}
            {breakdown?.subtotal_ht > 0 && <ShippingRow breakdown={breakdown} />}
            {breakdown?.digital_ttc > 0 && breakdown?.subtotal_ht === 0 && (
              <p className="text-[11px] text-sage-dark italic">Panier 100 % numérique : sans TVA, sans frais de livraison — téléchargement immédiat.</p>
            )}
          </div>
          {breakdown?.subtotal_ht > 0 && <FreeShippingProgress meta={breakdown?.shipping_meta} subtotal={breakdown?.subtotal_ht ?? cartTotal} />}
          <TierGiftProgress total={breakdown?.total_ttc ?? cartTotal} />
          {breakdown?.subtotal_ht > 0 && (
            <div className="mb-4">
              <ShippingInfoBlock variant="compact" />
            </div>
          )}
          <div className="border-t border-ink/10 pt-4 flex items-end justify-between gap-2">
            <span className="font-display text-base sm:text-lg">{tr(lang, "cart_total_ttc")}</span>
            <span className="font-display text-2xl sm:text-3xl whitespace-nowrap" data-testid="cart-total">
              {isFreeCheckout ? "0,00 €" : fmt(breakdown?.total_ttc ?? cartTotal)}
            </span>
          </div>
          {isFreeCheckout ? (
            <button
              data-testid="cart-free-checkout-btn"
              disabled={loading || blockedByPhysicalSales}
              onClick={checkout}
              className="btn-primary w-full justify-center mt-6 bg-sage-dark hover:bg-sage disabled:opacity-50"
              title={blockedByPhysicalSales ? "Retire les articles physiques du panier pour continuer" : ""}
            >
              {loading ? "..." : blockedByPhysicalSales ? "Vente physique suspendue" : "🎁 Valider ma commande gratuite"}
            </button>
          ) : (
            <button
              data-testid="cart-checkout-btn"
              disabled={loading || blockedByPhysicalSales || (breakdown?.digital_ttc > 0 && !digitalWaiver)}
              onClick={checkout}
              className="btn-primary w-full justify-center mt-6 disabled:opacity-50"
              title={blockedByPhysicalSales ? "Retire les articles physiques du panier pour continuer" : ""}
            >
              {loading ? "..." : blockedByPhysicalSales ? "Vente physique suspendue" : tr(lang, "cart_checkout")}
            </button>
          )}
          {breakdown?.digital_ttc > 0 && (
            <label className="flex items-start gap-2 mt-3 text-[11px] text-ink-soft" data-testid="digital-waiver">
              <input type="checkbox" checked={digitalWaiver} onChange={(e) => setDigitalWaiver(e.target.checked)} className="mt-0.5" data-testid="digital-waiver-checkbox" />
              <span>
                J'accepte que la ressource numérique soit livrée immédiatement et je renonce
                expressément à mon droit de rétractation de 14 jours (art. L221-28 du Code
                de la consommation).
              </span>
            </label>
          )}
          <Link to="/catalogue" className="block text-center text-sm text-ink-soft mt-4 hover:text-ink">
            ← {tr(lang, "cart_continue")}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Countdown affiché sous un code promo appliqué avec date de fin ─────────
function DiscountCountdown({ expiresAt, onExpired }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Refresh 1×/minute — pas besoin de rafraîchir chaque seconde
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const end = new Date(expiresAt).getTime();
  const remaining = end - now;
  useEffect(() => {
    if (remaining <= 0) onExpired?.();
  }, [remaining, onExpired]);
  if (remaining <= 0) return null;
  const days = Math.floor(remaining / (24 * 3600 * 1000));
  const hours = Math.floor((remaining % (24 * 3600 * 1000)) / (3600 * 1000));
  const mins = Math.floor((remaining % (3600 * 1000)) / (60 * 1000));
  // Format : "2j 4h" / "4h 32min" / "32min"
  let str;
  if (days > 0) str = `${days}j ${hours}h`;
  else if (hours > 0) str = `${hours}h ${mins}min`;
  else str = `${mins}min`;
  // Ambiance urgence : <24h = coral, <7j = terracotta, sinon ink-soft
  const urgent = days === 0;
  const soon = days > 0 && days < 7;
  const cls = urgent ? "text-coral-dark" : soon ? "text-terracotta" : "text-ink-soft";
  const icon = urgent ? "⏰" : soon ? "⏳" : "🕐";
  return (
    <p className={`mt-1.5 text-[11px] flex items-center gap-1 ${cls}`} data-testid="discount-countdown">
      <span>{icon}</span>
      <span>Ce code expire dans <strong className="tabular-nums">{str}</strong></span>
    </p>
  );
}


function Row({ label, value, accent }) {
  const { fmt } = useApp();
  return (
    <div className="flex justify-between">
      <span className="text-ink-soft">{label}</span>
      <span className={accent ? "text-coral-dark" : ""}>{fmt(value)}</span>
    </div>
  );
}

function LoyaltyRedeem({ account, tickets, setTickets, subtotal }) {
  const balance = account.balance;
  const sub = Number(subtotal || 0);
  // Compute live max per denomination given the current selection
  const currentPts = (tickets["20"] || 0) * 20 + (tickets["100"] || 0) * 100 + (tickets["200"] || 0) * 200;
  const currentEur = (tickets["20"] || 0) * 1  + (tickets["100"] || 0) * 5   + (tickets["200"] || 0) * 10;
  const remainingPts = balance - currentPts;
  const remainingEur = sub - currentEur;

  const TICKETS = [
    { pts: 20,  eur: 1,  tone: "sage" },
    { pts: 100, eur: 5,  tone: "pedablue" },
    { pts: 200, eur: 10, tone: "coral" },
  ];

  const setN = (key, delta) => {
    setTickets((prev) => {
      const cur = Number(prev[key] || 0);
      const t = TICKETS.find((x) => String(x.pts) === key);
      // If incrementing, verify enough orthopoints + enough subtotal
      if (delta > 0) {
        if (remainingPts < t.pts || remainingEur < t.eur) return prev;
      }
      const next = Math.max(0, cur + delta);
      return { ...prev, [key]: next };
    });
  };
  const reset = () => setTickets({});
  const totalTickets = (tickets["20"] || 0) + (tickets["100"] || 0) + (tickets["200"] || 0);

  return (
    <div
      data-testid="loyalty-redeem-panel"
      className="rounded-2xl border border-coral/30 bg-gradient-to-br from-coral/5 to-pedablue/5 px-4 py-4 mb-4"
    >
      <div className="flex items-baseline justify-between mb-3 text-sm">
        <span className="font-medium text-coral-dark inline-flex items-center gap-1.5">
          <Gift size={14} /> {balance} orthopoints disponibles
        </span>
        {totalTickets > 0 && (
          <button onClick={reset} className="text-xs underline text-ink-soft hover:text-coral-dark" data-testid="loyalty-reset">Annuler</button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {TICKETS.map((t) => {
          const n = Number(tickets[String(t.pts)] || 0);
          const canAdd = remainingPts >= t.pts && remainingEur >= t.eur;
          return (
            <div key={t.pts} className="text-center">
              <OrthoTicket pts={t.pts} eur={t.eur} tone={t.tone} small count={n} disabled={n === 0 && !canAdd} />
              <div className="flex items-center justify-center gap-1.5 mt-2">
                <button
                  onClick={() => setN(String(t.pts), -1)}
                  disabled={n === 0}
                  className="w-7 h-7 rounded-full bg-white border border-ink/15 text-ink disabled:opacity-30 hover:bg-ink hover:text-white transition text-sm"
                  data-testid={`loyalty-minus-${t.pts}`}
                >−</button>
                <button
                  onClick={() => setN(String(t.pts), +1)}
                  disabled={!canAdd}
                  className="w-7 h-7 rounded-full bg-coral-dark text-white disabled:opacity-30 hover:bg-coral transition text-sm font-semibold"
                  data-testid={`loyalty-plus-${t.pts}`}
                >+</button>
              </div>
            </div>
          );
        })}
      </div>
      {totalTickets > 0 && (
        <p className="text-xs text-coral-dark mt-3 text-center font-medium" data-testid="loyalty-total-line">
          {totalTickets} ticket{totalTickets > 1 ? "s" : ""} sélectionné{totalTickets > 1 ? "s" : ""} — Économie : −{currentEur}€ ({currentPts} orthopoints)
        </p>
      )}
    </div>
  );
}

function ShippingRow({ breakdown }) {
  const { fmt } = useApp();
  const shipHT = breakdown?.shipping_ht ?? 0;
  const shipTTC = breakdown?.shipping_ttc ?? shipHT;
  const free = breakdown?.shipping_meta?.free;
  if (free) {
    return (
      <div className="flex justify-between items-center" data-testid="cart-shipping-row">
        <span className="text-ink-soft inline-flex items-center gap-1.5"><Truck size={14} /> Frais de port</span>
        <span className="inline-flex items-center gap-1 text-sage font-medium"><Gift size={14} /> Offerts</span>
      </div>
    );
  }
  return (
    <div className="flex justify-between" data-testid="cart-shipping-row">
      <span className="text-ink-soft inline-flex items-center gap-1.5"><Truck size={14} /> Frais de port</span>
      <span>{fmt(shipTTC)}</span>
    </div>
  );
}

function TierGiftProgress({ total }) {
  const [tiers, setTiers] = useState({ unlocked: [], next: null, missing_to_next: 0 });
  useEffect(() => {
    if (!total || total <= 0) return;
    api.get(`/tier-gifts/for-cart?total_ttc=${total}`)
      .then((r) => setTiers(r.data))
      .catch(() => {});
  }, [total]);
  if (!tiers.next && tiers.unlocked.length === 0) return null;
  return (
    <div className="bg-coral/10 border border-coral/30 rounded-2xl p-3 mb-4" data-testid="tier-gift-progress">
      {tiers.unlocked.length > 0 && (
        <p className="text-xs text-coral-dark font-medium mb-1">
          🎁 Cadeau débloqué : {tiers.unlocked[tiers.unlocked.length - 1].label}
        </p>
      )}
      {tiers.next && (
        <p className="text-xs text-ink-soft">
          Plus que <strong className="text-coral-dark">{tiers.missing_to_next.toFixed(2)} €</strong> pour débloquer votre ressource gratuite dans la catégorie <strong>{tiers.next.label}</strong>
        </p>
      )}
    </div>
  );
}

function FreeShippingProgress({ meta, subtotal }) {  if (!meta) return null;
  if (meta.free) {
    return (
      <div
        data-testid="free-shipping-badge"
        className="rounded-2xl bg-sage/10 border border-sage/30 px-4 py-3 mb-4 flex items-center gap-2 text-sm"
      >
        <Gift size={18} className="text-sage shrink-0" />
        <span className="text-sage-dark font-medium">Livraison offerte appliquée — votre commande dépasse le seuil de {meta.free_threshold}€ !</span>
      </div>
    );
  }
  const threshold = Number(meta.free_threshold || 100);
  const sub = Number(subtotal || 0);
  const pct = Math.min(100, Math.round((sub / threshold) * 100));
  const missing = Number(meta.missing_to_free ?? Math.max(threshold - sub, 0));
  const zoneLabel = {
    EUROPE: " (Europe)", ASIA: " (Asie)", AFRICA: " (Afrique)",
    AMERICAS: " (Amériques)", OCEANIA: " (Océanie)",
    EU: " (UE)", FR: "", WORLD: "",
  }[meta.continent || meta.zone] || "";
  return (
    <div
      data-testid="free-shipping-progress"
      className="rounded-2xl bg-coral/5 border border-coral/20 px-4 py-3 mb-4"
    >
      <div className="flex items-center gap-2 text-sm mb-2">
        <Truck size={16} className="text-coral-dark shrink-0" />
        <span className="text-ink">
          Plus que <strong className="text-coral-dark">{missing.toFixed(2)}€</strong> pour la livraison offerte{zoneLabel}
        </span>
      </div>
      <div className="h-1.5 bg-coral/15 rounded-full overflow-hidden">
        <div
          className="h-full bg-coral-dark transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
